import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  Link as MUILink,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Add, Home, Refresh, Assignment } from '@mui/icons-material';
import { useLossRecordList } from '@/hooks';
import { usePageTitle } from '@/hooks/usePageTitle';
import { getErrorMessage } from '@/utils/httpClient';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { DD200Modal, DA7923Modal, CompleteModal } from '@/components/asset-loss-management';
import {
  getLossTypeLabel,
  type LossTypeLabel,
} from '@/components/asset-loss-management/lossTypeLabels';
import type { LossRecordViewRow, WorkflowStatus } from '@/services/lossRecordService';
import { multiWordMatch } from '@/utils/multiWordMatch';
import { usePersonLabels } from '@/hooks/usePersonLabels';

type TransactionTypeFilter = LossTypeLabel | 'all';
type WorkflowStatusFilter = WorkflowStatus | 'all';

export const AssetLossManagementPage: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('Asset Loss Management');
  const person = usePersonLabels('front_window');

  const [transactionTypeFilter, setTransactionTypeFilter] = useState<TransactionTypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<WorkflowStatusFilter>('all');
  const [individualFilter, setIndividualFilter] = useState('');
  const [selectedRecord, setSelectedRecord] = useState<LossRecordViewRow | null>(null);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [dd200ModalOpen, setDD200ModalOpen] = useState(false);
  const [da7923ModalOpen, setDA7923ModalOpen] = useState(false);

  const isViewer = useWriteGuard();
  const { data: records = [], isLoading, isFetching, error, refetch } = useLossRecordList();

  const dateTimeFormatter = useMemo(
    () => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
    [],
  );

  const formatDateTime = useCallback(
    (value: string | null) => {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return dateTimeFormatter.format(date);
    },
    [dateTimeFormatter],
  );

  const filteredRecords = useMemo(() => {
    let result = records;
    if (transactionTypeFilter !== 'all') {
      result = result.filter(
        (r) =>
          getLossTypeLabel({
            transactionType: r.transaction_type,
            includeContaminationMemo: r.include_contamination_memo,
          }) === transactionTypeFilter,
      );
    }
    if (statusFilter !== 'all') {
      result = result.filter((r) => r.workflow_status === statusFilter);
    }
    if (individualFilter.trim()) {
      const search = individualFilter.toLowerCase().trim();
      result = result.filter(
        (r) =>
          multiWordMatch(
            `${r.person_display_name ?? ''} ${r.person_identifier ?? ''}`,
            search,
          ),
      );
    }
    return result;
  }, [records, transactionTypeFilter, statusFilter, individualFilter]);

  const handleOpenDD200Modal = useCallback(() => {
    setDD200ModalOpen(true);
  }, []);

  const handleCloseDD200Modal = useCallback(() => {
    setDD200ModalOpen(false);
  }, []);

  const handleDD200Success = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleOpenDA7923Modal = useCallback(() => {
    setDA7923ModalOpen(true);
  }, []);

  const handleCloseDA7923Modal = useCallback(() => {
    setDA7923ModalOpen(false);
  }, []);

  const handleDA7923Success = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleOpenRecord = useCallback((record: LossRecordViewRow) => {
    setSelectedRecord(record);
    setCompleteModalOpen(true);
  }, []);

  const handleCloseCompleteModal = useCallback(() => {
    setCompleteModalOpen(false);
    setSelectedRecord(null);
  }, []);

  const handleCompleteSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const columns = useMemo<GridColDef<LossRecordViewRow>[]>(
    () => [
      {
        field: 'transaction_type',
        headerName: 'Type',
        width: 160,
        renderCell: (params) => (
          <Chip
            size="small"
            label={getLossTypeLabel({
              transactionType: params.row.transaction_type,
              includeContaminationMemo: params.row.include_contamination_memo,
            })}
            color={
              params.row.transaction_type === 'DA7923'
                ? 'secondary'
                : params.row.include_contamination_memo
                  ? 'warning'
                  : 'primary'
            }
            variant="outlined"
          />
        ),
      },
      {
        field: 'workflow_status',
        headerName: 'Status',
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={
              params.row.workflow_status === 'suspense'
                ? 'Suspense'
                : params.row.workflow_status === 'corrected'
                  ? 'Corrected'
                  : 'Complete'
            }
            color={
              params.row.workflow_status === 'complete'
                ? 'success'
                : params.row.workflow_status === 'corrected'
                  ? 'info'
                  : 'warning'
            }
            variant={params.row.workflow_status === 'suspense' ? 'outlined' : 'filled'}
          />
        ),
      },
      {
        field: 'person_display_name',
        headerName: person.label,
        flex: 1.5,
        minWidth: 200,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', py: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {params.row.person_display_name ?? '—'}
            </Typography>
            {params.row.person_identifier && (
              <Typography variant="caption" color="text.secondary">
                {params.row.person_identifier}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'asset_count',
        headerName: 'Assets',
        width: 100,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'comments',
        headerName: 'Comments',
        flex: 2,
        minWidth: 200,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.row.comments || '—'}
          </Typography>
        ),
      },
      {
        field: 'suspense_at',
        headerName: 'Created',
        width: 180,
        valueFormatter: (value: string | null) => formatDateTime(value),
      },
      {
        field: 'completed_at',
        headerName: 'Completed',
        width: 180,
        valueFormatter: (value: string | null) => formatDateTime(value),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleOpenRecord(params.row)}
            sx={{ textTransform: 'none' }}
          >
            {!isViewer && params.row.workflow_status === 'suspense' ? 'Complete' : 'View'}
          </Button>
        ),
      },
    ],
    [formatDateTime, handleOpenRecord, isViewer],
  );

  return (
    <Container maxWidth={false} sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MUILink
            color="inherit"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </MUILink>
          <Typography color="text.primary">Asset Loss Management</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Assignment color="primary" sx={{ fontSize: 36 }} />
          <Typography variant="h4" component="h1">
            Asset Loss Management
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
          Manage FLIPL, Statement of Charges, and MFR workflows for lost, destroyed, or
          unserviceable assets.
        </Typography>
      </Box>

      <Paper elevation={1} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  value={transactionTypeFilter}
                  label="Transaction Type"
                  onChange={(e) =>
                    setTransactionTypeFilter(e.target.value as TransactionTypeFilter)
                  }
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="FLIPL">FLIPL</MenuItem>
                  <MenuItem value="Statement of Charges">Statement of Charges</MenuItem>
                  <MenuItem value="MFR">MFR</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value as WorkflowStatusFilter)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="suspense">Suspense</MenuItem>
                  <MenuItem value="complete">Complete</MenuItem>
                  <MenuItem value="corrected">Corrected</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                label={person.label}
                placeholder="Search by name or ID..."
                value={individualFilter}
                onChange={(e) => setIndividualFilter(e.target.value)}
                sx={{ minWidth: 200 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => refetch()}
                disabled={isFetching}
              >
                Refresh
              </Button>
              {!isViewer && (
              <Button variant="contained" startIcon={<Add />} onClick={handleOpenDD200Modal}>
                Initiate FLIPL
              </Button>
              )}
              {!isViewer && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Add />}
                onClick={handleOpenDA7923Modal}
              >
                Statement of Charges
              </Button>
              )}
            </Box>
          </Box>

          {error && (
            <Alert severity="error">{getErrorMessage(error) ?? 'Failed to load records.'}</Alert>
          )}

          <Box sx={{ height: 520 }}>
            <DataGrid
              rows={filteredRecords}
              columns={columns}
              loading={isLoading || isFetching}
              density="comfortable"
              disableRowSelectionOnClick
              autoHeight={false}
              getRowHeight={() => 'auto'}
              localeText={{
                noRowsLabel: isLoading
                  ? 'Loading records...'
                  : 'No asset loss management records yet. Use the buttons above to create one.',
              }}
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: 'background.default',
                },
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          </Box>
        </Stack>
      </Paper>

      <DD200Modal
        open={dd200ModalOpen}
        onClose={handleCloseDD200Modal}
        onSuccess={handleDD200Success}
      />

      <DA7923Modal
        open={da7923ModalOpen}
        onClose={handleCloseDA7923Modal}
        onSuccess={handleDA7923Success}
      />

      <CompleteModal
        open={completeModalOpen}
        record={selectedRecord}
        onClose={handleCloseCompleteModal}
        onSuccess={handleCompleteSuccess}
      />
    </Container>
  );
};
